export const importExportArticle = {
    id: 'import-export',
    title: 'Импорт и экспорт',
    category: 'Режим рисования',
    requiredModule: null,
    sandboxType: null,
    summary: 'Загрузка и выгрузка пространственных данных в формате GeoJSON: резервное копирование, перенос между проектами и выборочный экспорт объектов.',
    markdown: `
> **Инструменты «Импорт» и «Экспорт»** предназначены для создания резервных копий карты посёлка, быстрого переноса объектов между рабочими пространствами, а также точечной выгрузки нужных очередей или групп участков.

---

## 1. Зачем нужны импорт и экспорт

:::grid cols=2
:::card icon="download" title="1. Резервное копирование"
Регулярно сохраняйте текущее состояние генплана в файл \`map_data.json\` на свой компьютер, чтобы гарантировать 100% сохранность и защиту от случайных удалений.
:::

:::card icon="layers" title="2. Перенос между пространствами"
Легко переносите слои участков, строений и дорог из одного рабочего пространства или тестового стенда в другой в пару кликов.
:::

:::card icon="file-text" title="3. Дублирование и концепты"
Создавайте резервную копию перед масштабными изменениями карты, чтобы при необходимости быстро вернуться к исходному варианту.
:::

:::card icon="settings" title="4. Выборочный экспорт (SPD+)"
Нужно сохранить только участки первой очереди или свободные дома? При активном модуле **SPD+** выделите нужные объекты и выгрузите отдельный целевой файл.
:::
:::

---

## 2. Как работает экспорт данных

В панели инструментов выберите кнопку **«Экспорт»** (иконка со стрелкой вверх). Система автоматически определяет режим выгрузки:

:::grid cols=2
:::card icon="file-text" title="Полный экспорт карты (Базовая версия)"
Если ни один объект не выбран (или используется базовая версия системы), нажатие кнопки «Экспорт» формирует и скачивает файл \`map_data.json\`, содержащий **все видимые объекты** на карте (участки, здания, линии, метки) со всеми их свойствами.
:::

:::card icon="view" title="Точечный экспорт (Модуль SPD+)"
При активированном модуле расширения **SPD+** и наличии **выделенных объектов**, система сформирует файл \`selected_map_data.json\`, содержащий **только выбранные элементы**.
:::
:::

> 💡 **Обратите внимание:**  
> Инструменты множественного выделения объектов (чекбоксы в «Менеджере объектов», Shift+клик по карте) и **точечный выборочный экспорт** являются функционалом модуля расширения **SPD+**. В базовой версии системы экспорт всегда сохраняет всю карту целиком.

### Как выполнить точечный экспорт (в модуле SPD+):
1. Выделите нужные объекты одним из способов:
   * **Через «Менеджер объектов»** (режим *Данные*): отметьте чекбоксами конкретные участки, дома или линии.
   * **Прямо на карте:** кликайте по объектам с зажатой клавишей **Shift** для множественного выбора.
2. Нажмите кнопку **«Экспорт»** в панели инструментов.
3. В браузере мгновенно скачается файл \`selected_map_data.json\`, а система покажет уведомление с точным числом экспортированных объектов.

---

## 3. Как работает импорт данных

Инструмент **«Импорт»** доступен в базовой версии и позволяет загрузить ранее сохранённый файл или перенести набор геоданных.

:::card icon="click" title="Пошаговая загрузка данных:"
1. **Нажмите «Импорт»:** кликните по кнопке с иконкой загрузки в блоке инструментов «Данные».
2. **Выберите файл:** укажите файл с расширением \`.json\` или \`.geojson\` на вашем компьютере (стандартная структура *FeatureCollection*).
3. **Окно предпросмотра:** система откроет модальное окно импорта, где наглядно покажет:
   * Количество найденных объектов.
   * Типы геометрии (полигоны участков, здания, линейные сети, точки).
   * Распознанные атрибуты и свойства.
4. **Подтверждение:** нажмите кнопку подтверждения, и объекты моментально нанесутся на карту с автоматическим расчетом площадей и длин.
:::

> ⚠️ **Важно: совместимость с файлами из сторонних программ**  
> Модуль импорта оптимизирован для работы со стандартными файлами GeoJSON, сформированными **в самой системе IChis SPD** (координатная система WGS 84, стандартная структура \`FeatureCollection\`, простые типы \`Polygon\`, \`LineString\`, \`Point\`).  
> Файлы, выгруженные напрямую из внешних профессиональных САПР и ГИС-пакетов (AutoCAD, MapInfo, специализированные кадастровые комплексы), могут использовать специфические местные системы координат (МСК/СК-42), сложные составные геометрии или проприетарные поля. При попытке загрузки таких файлов без предварительной подготовки система может выдать ошибку или не распознать часть объектов.

---

## 4. Сохраняемые параметры и структура файла

При экспорте в GeoJSON полностью сохраняются не только точные пространственные координаты вершин, но и все метаданные из паспорта каждого объекта:

| Параметр | Описание в экспорте |
| :--- | :--- |
| **Геометрия** | Точные координаты в стандартной системе WGS 84 (EPSG:4326). |
| **Тип объекта** | Земельный участок (\`plot\`), здание (\`building\`), линия (\`line\`) или метка (\`marker\`). |
| **Кадастровые данные** | Кадастровый номер, адрес, площадь и периметр. |
| **Коммерческие статусы** | Статус продажи (свободен, бронь, продан), стоимость, этажность и назначение. |
| **Оформление** | Пользовательский цвет заливки, обводки, прозрачность и выбранная иконка метки. |
| **Пользовательские поля** | Любые кастомные атрибуты и описания, заполненные оператором в паспорте объекта. |
`
};
